import axios from 'axios';
import toast from 'react-hot-toast';
import { tokenManager } from '@/auth';
import { AUTH_REFRESH_URL } from '@gokrew/models/constants';
import { captureException } from '@sentry/react';
import {
  getUserErrorMessage,
  getSentryErrorMessage,
} from '@gokrew/ui/utility/utility';
import i18n from '@/i18n';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// Send the active app language to the backend so translated validation/business
// error messages come back in the user's selected locale.
api.interceptors.request.use((config) => {
  config.params = { lang: i18n.language || 'en', ...config.params };
  return config;
});

// Attach a valid token automatically, refreshing first if close to expiry
api.interceptors.request.use(async (config) => {
  if (config.url?.includes(AUTH_REFRESH_URL)) return config;

  if (!tokenManager.getToken()) return config;

  const token = await tokenManager.getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors globally
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error.response?.status;
    const hasToken = tokenManager.getToken() !== null;
    const url = error.config?.url;

    if (status === 401 && hasToken) {
      const originalRequest = error.config;

      if (originalRequest._retry) {
        tokenManager.triggerSessionExpired();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const newToken = await tokenManager.getValidToken();

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      tokenManager.triggerSessionExpired();
      return Promise.reject(error);
    }

    // Direct-message failures are handled inline. Never serialize message
    // request bodies into generic telemetry or redirect away from the draft.
    if (/\/messaging(?:\/|\?|$)/.test(url ?? '')) {
      return Promise.reject(error);
    }

    // The caller shows this error itself (e.g. My team's "not in your team"
    // page, an approval that moved): no second report as a toast, and no 403
    // redirect away from the screen that explains it.
    if (status && error.config?.quietStatuses?.includes(status)) {
      return Promise.reject(error);
    }

    toast.dismiss();
    toast.error(getUserErrorMessage(error, i18n));

    const shouldLogToSentry = !status || status >= 500 || status === 400;

    const sentryMessage = getSentryErrorMessage(error, 'Employee Error');

    if (shouldLogToSentry) {
      captureException(new Error(sentryMessage), {
        tags: {
          type: 'api_error',
          endpoint: url,
          method: error.config?.method,
          status: status?.toString(),
        },
        extra: {
          originalError: error,
          response: error.response?.data,
        },
      });
    }

    if (status === 403) {
      window.location.href = '/';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
